import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { CableOutlined } from '@mui/icons-material';

/**
 * Reusable empty state component with themed icon, message, and optional CTA.
 *
 * Props:
 *   icon       - React node (MUI icon, defaults to disconnected cable)
 *   title      - string (bold heading)
 *   message    - string (secondary description)
 *   ctaText    - string (button label, optional)
 *   onCtaClick - function (button handler, optional)
 *   ctaHref    - string (link destination, optional — alternative to onCtaClick)
 */
const EmptyState = ({ icon, title, message, ctaText, onCtaClick, ctaHref }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 6,
      px: 3,
      textAlign: 'center',
      border: '1px dashed',
      borderColor: 'secondary.light',
      bgcolor: 'background.default',
      borderRadius: 0,
    }}
  >
    <Box
      sx={{
        mb: 2,
        color: 'primary.main',
        opacity: 0.6,
        '& svg': { fontSize: 48 },
      }}
    >
      {icon || <CableOutlined />}
    </Box>
    <Typography
      variant="h6"
      sx={{
        color: 'text.primary',
        mb: 1,
        fontFamily: '"Bebas Neue", sans-serif',
        letterSpacing: '0.05em',
      }}
    >
      {title}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: 'text.secondary',
        maxWidth: 360,
        lineHeight: 1.7,
        mb: ctaText ? 3 : 0,
      }}
    >
      {message}
    </Typography>
    {ctaText && (
      <Button
        variant="outlined"
        size="small"
        onClick={onCtaClick}
        href={ctaHref}
        sx={{ px: 3 }}
      >
        {ctaText}
      </Button>
    )}
  </Box>
);

export default EmptyState;
